import { useMemo } from 'react';
import { useFetchData } from '../hooks/useFetchData';
import { v2ServicePath } from '../config';

// ── normalizers ────────────────────────────────────────────────────────────

function normalizeV2Session(s) {
  const traceCount  = s.trace_count ?? 0;
  const errorCount  = s.error_count ?? 0;
  const inputTokens = s.total_input_tokens ?? 0;
  const outputTokens = s.total_output_tokens ?? 0;
  const sessionStart = s.first_seen ? new Date(s.first_seen) : null;
  const sessionEnd   = s.last_seen  ? new Date(s.last_seen)  : null;
  const totalDurationMs = sessionStart && sessionEnd ? Math.max(0, sessionEnd - sessionStart) : null;
  return {
    session_id:            s.session_id,
    user_id:               s.user_id ?? null,
    session_start:         s.first_seen ?? null,
    trace_count:           traceCount,
    total_tokens:          inputTokens + outputTokens,
    total_duration_ms:     totalDurationMs,
    cost:                  s.cost ?? 0,
    error_rate:            traceCount > 0 ? Math.round((errorCount / traceCount) * 10000) / 100 : 0,
    error_spans:           errorCount,
    total_spans:           s.span_count ?? 0,
    has_prompt_injection:  false,
    has_data_exfiltration: false,
    workflow_name:         null,
    agent_names:           Array.isArray(s.agent_names)  ? s.agent_names  : [],
    model_names:           Array.isArray(s.model_names)  ? s.model_names  : [],
    detections:            Array.isArray(s.detections)   ? s.detections   : [],
  };
}

// ── hook ───────────────────────────────────────────────────────────────────

/**
 * Fetches the session list for a service. Pass `enabled=false` to skip the fetch.
 *
 * Returns { sessions, loading, error }
 *   sessions — null while loading/not fetched, [] when empty, array when populated
 */
export function useSessionList(selectedAppId, enabled = true) {
  const endpoint = v2ServicePath(selectedAppId, '/sessions');

  const { data, loading, error } = useFetchData({
    endpoint,
    enabled: enabled && !!selectedAppId,
  });

  const sessions = useMemo(() => {
    if (!data) return null;
    return Array.isArray(data.data) ? data.data.map(normalizeV2Session) : null;
  }, [data]);

  return { sessions, loading, error };
}
